import { gql } from '__generated__'

export const GetScreeningInterviewTeams = gql(/* GraphQL */ `
    query GetScreeningInterviewTeams {
        screeningInterviewTeams
    }
`)

export const GetCandidateProfileForRecruiter = gql(/* GraphQL */ `
    query GetCandidateProfileForRecruiter($candidateId: ID) {
        candidate(id: $candidateId) {
            id
            lastLogin
            preferredEmail
            otherAnalysts {
                analyst
                createdByUser {
                    firstName
                    lastName
                    nickname
                }
                updatedAt
            }
            startYear
            updatedAt
            recruiterStarRating
            user {
                firstName
                lastName
                id
                email
                phone
                profileImageKey
                nickname
            }
            linkedinUrl
            prefersWorkEmail
            programs {
                id
                value
            }
            currentWorkExperience {
                workEmail
            }
            testScores {
                satMathScore
                satVerbalScore
                satWritingScore
                satTotal
                actScore
                gmatScore
                greTotalScore
                greVerbalScore
                greQuantitativeScore
                greWritingScore
                tookActSatTests
                remembersTakingActSatTests
            }
            personalityScores {
                id
                ccatRawScore
                ccatPercentile
                updatedByUser {
                    id
                    preferredName
                }
                createdByUser {
                    id
                    preferredName
                }
                updatedAt
                createdAt
                eppScores {
                    id
                    eppCategoryId
                    score
                }
            }
            recruiterReviewSummary {
                averageRating
                ratingCount
                stats {
                    averageRating
                    count
                    noteType
                }
            }
            internalProfileImageKey
            recruitingStatus {
                status
            }
            outreachStatus {
                status
                nonPoachDate
            }
            systemStatus {
                status
            }
            candidateIntakes {
                completionDate
            }
            otherAnalysts {
                analyst
            }
            analystClassYear
            education {
                id
                college {
                    id
                    value
                }
                degrees {
                    id
                    value
                }
                majors {
                    major {
                        id
                        value
                    }
                }
                degreeTypeId
                graduationDate
                gpa
                gpaScale
            }
            workExperience {
                id
                isCurrent
                experienceDescription
                company {
                    id
                    value
                }
                companyOffice {
                    id
                    officeLocation {
                        id
                        value
                    }
                }
                industries {
                    topTier
                    industry {
                        id
                        value
                    }
                }
                sectors {
                    topTier
                    sector {
                        id
                        value
                    }
                }
                title {
                    id
                    value
                    isStudent
                }
                workEmail
                workPhone
                startDate
                endDate
            }
            notes {
                isActive
                isPinned
                noteId
                noteData
                starRating
                excludeFromEvaluation
                templateId
                template {
                    id
                    templateType
                    hasStarRating
                    isScreeningType
                    canEdit
                }
                noteType
                note {
                    ... on CandidateNote {
                        id
                        candidateId
                        companyId
                        company {
                            id
                            value
                        }
                        rosterId
                        roster {
                            id
                            rosterName
                        }
                    }
                    ... on CandidateGroupNotes {
                        id
                        groupNote {
                            id
                            candidates {
                                id
                                user {
                                    id
                                    firstName
                                    nickname
                                    lastName
                                }
                            }
                            company {
                                id
                                value
                            }
                            roster {
                                id
                                rosterName
                            }
                        }
                    }
                }
                createdAt
                updatedAt
                createdBy
                createdByUser {
                    id
                    firstName
                    nickname
                    lastName
                }
                updatedBy
                updatedByUser {
                    id
                    firstName
                    nickname
                    lastName
                }
            }
            documents {
                createdAt
                updatedAt
                candidateId
                documentType
                documentKey
                id
            }
            interviewBucket {
                screeningInterviewTeam
                bucket
                candidateId
                reason
            }
            staffers {
                staffer {
                    id
                    firstName
                    lastName
                    title {
                        id
                        value
                    }
                    email
                    phone
                }
            }
            isDeferredMba
        }
    }
`)

export const GetEppCategories = gql(/* GraphQL */ `
    query GetEppCategories {
        eppCategories {
            id
            value
        }
    }
`)

export const GetNoteContent = gql(/* GraphQL */ `
    query getNoteContent($noteId: ID!) {
        getNoteContent(noteId: $noteId) {
            content
        }
    }
`)

export const UpdateCandidateProfile = gql(/* GraphQL */ `
    mutation UpdateCandidateProfile(
        $id: ID!
        $firstName: String!
        $lastName: String!
        $nickname: String!
        $email: String!
        $phone: String!
        $linkedinUrl: String!
        $recruitingStatus: String!
        $outreachStatus: OutreachStatus!
        $systemStatus: SystemStatus
        $nonPoachDate: DateTimeISO
        $screeningInterviewTeam: String
        $prefersWorkEmail: Boolean
    ) {
        updateCandidateProfile(
            id: $id
            firstName: $firstName
            lastName: $lastName
            nickname: $nickname
            email: $email
            phone: $phone
            linkedinUrl: $linkedinUrl
            recruitingStatus: $recruitingStatus
            outreachStatus: $outreachStatus
            systemStatus: $systemStatus
            nonPoachDate: $nonPoachDate
            screeningInterviewTeam: $screeningInterviewTeam
            prefersWorkEmail: $prefersWorkEmail
        ) {
            success
            message
        }
    }
`)

export const UpdateProfileViewHistory = gql(/* GraphQL */ `
    mutation TrackRecruiterCandidateProfileView($candidateId: String!) {
        trackRecruiterCandidateProfileView(candidateId: $candidateId) {
            success
        }
    }
`)

export const UpdateCandidateTestScores = gql(/* GraphQL */ `
    mutation updateCandidateTestScores(
        $candidateId: ID!
        $satMathScore: Float
        $satVerbalScore: Float
        $satWritingScore: Float
        $satTotal: Float
        $actScore: Float
        $gmatScore: Float
        $greTotalScore: Float
        $greVerbalScore: Float
        $greQuantitativeScore: Float
        $greWritingScore: Float
        $tookActSatTests: Boolean
        $remembersTakingActSatTests: Boolean
    ) {
        updateCandidateTestScores(
            candidateId: $candidateId
            satMathScore: $satMathScore
            satVerbalScore: $satVerbalScore
            satWritingScore: $satWritingScore
            satTotal: $satTotal
            actScore: $actScore
            gmatScore: $gmatScore
            greTotalScore: $greTotalScore
            greVerbalScore: $greVerbalScore
            greQuantitativeScore: $greQuantitativeScore
            greWritingScore: $greWritingScore
            tookActSatTests: $tookActSatTests
            remembersTakingActSatTests: $remembersTakingActSatTests
        ) {
            candidateId
            satMathScore
            satVerbalScore
            satWritingScore
            satTotal
            actScore
            gmatScore
            greTotalScore
            greVerbalScore
            greQuantitativeScore
            greWritingScore
            tookActSatTests
            remembersTakingActSatTests
        }
    }
`)

export const UpdateJobBoardResponse = gql(/* GraphQL */ `
    mutation UpdateJobBoardResponse(
        $candidateJobBoardId: ID!
        $isInterested: Boolean
        $comments: String
        $notInterestedReason: JobBoardNotInterestedReasons
        $companyOfficePrefIds: [ID!]
        $sectorPrefIds: [ID!]
    ) {
        updateCandidatesJobBoardResponse(
            candidateJobBoardId: $candidateJobBoardId
            isInterested: $isInterested
            comments: $comments
            notInterestedReason: $notInterestedReason
            companyOfficePrefIds: $companyOfficePrefIds
            sectorPrefIds: $sectorPrefIds
        ) {
            success
            message
        }
    }
`)

export const UpdateCandidateIsDeferredMba = gql(/* GraphQL */ `
    mutation UpdateCandidateIsDeferredMba(
        $candidateId: ID!
        $isDeferredMba: Boolean!
    ) {
        updateCandidateIsDeferredMba(
            candidateId: $candidateId
            isDeferredMba: $isDeferredMba
        ) {
            success
            message
        }
    }
`)

export const UpdateCandidatePrograms = gql(/* GraphQL */ `
    mutation updateCandidatePrograms($programIds: [ID!]!, $candidateId: ID!) {
        updateCandidatePrograms(
            programIds: $programIds
            candidateId: $candidateId
        ) {
            id
            candidateId
            program {
                id
                value
            }
        }
    }
`)

export const UpdateCandidatePersonalityScores = gql(/* GraphQL */ `
    mutation UpdateCandidatePersonalityScores(
        $candidateId: ID!
        $ccatScores: UpdateCcatScores
        $eppScores: [EppScoreInput!]
    ) {
        updateCandidatePersonalityScores(
            candidateId: $candidateId
            ccatScores: $ccatScores
            eppScores: $eppScores
        ) {
            success
            message
        }
    }
`)
